import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from "../context/AuthContext";
import { authenticatedFetch } from '../context/apiClient';
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChevronDown, Download, MoreHorizontal, RefreshCw, AlertCircle } from 'lucide-react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';
import { useParams } from 'react-router-dom';

const AssessmentReport = () => {
  const [expandedItems, setExpandedItems] = useState({});
  const [tasks, setTasks] = useState([]);
  const { scan_id: routeScanId } = useParams();
  const [activeScanId, setActiveScanId] = useState(routeScanId || null);

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportError, setReportError] = useState(null);
  const { user, logout } = useAuth();
  const [activeCategory, setActiveCategory] = useState('');

  const [downloading, setDownloading] = useState(false);

  const toggleExpand = (index) => {
    setExpandedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const fetchTasks = useCallback(async () => {
    if (!user?.token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch('/api/tasks', {}, user.token, logout);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setTasks(data);
      if (data.length > 0 && !activeScanId) {
        setActiveScanId(data[0].id);
      }
    } catch (error) {
      console.error("Failed to fetch tasks:", error);
      setError("Failed to load tasks. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [user, logout,activeScanId]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);
  
  useEffect(() => {
    const fetchReportData = async () => {
      //console.log("Fetching report data for scan ID:", activeScanId);
      if (!activeScanId || !user?.token) return;
      console.log("Fetching report data for scan ID:", activeScanId);
      setReportLoading(true);
      setReportError(null);
      try {
        const response = await authenticatedFetch(`/api/assessment-report/${activeScanId}`, {}, user.token, logout);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setReportData(data);
        if (data.riskCategories && data.riskCategories.length > 0) {
          setActiveCategory(data.riskCategories[0].key);
        }
      } catch (error) {
        console.error("Failed to fetch report data:", error);
        setReportError("Failed to load report data. Please try again later.");
      } finally {
        setReportLoading(false);
      }
    }
    fetchReportData();
  }, [activeScanId, user, logout]);

  const flaggedExamples = reportData?.riskExamples?.[activeCategory] || [];
  const radarChartData = reportData?.radarChart?.radarChart;
  const riskBreakdown = reportData?.riskBreakdown || [];

  const exportToPDF = async () => {
    setDownloading(true); // Start loading
    try {
      const response = await authenticatedFetch(`/api/export-pdf/${activeScanId}`, {}, user.token, logout);
      
      if (!response.ok) throw new Error('Download failed');
  
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_${activeScanId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setDownloading(false); // Stop loading regardless of success/fail
    }
  };

  return (
    <div className="min-h-screen  p-6">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <h1 className="text-2xl font-semibold text-gray-900 mb-6">Assessment Report Management</h1>

        {/* Task List */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg relative mb-6" role="alert">
            <strong className="font-bold">Error:</strong>
            <span className="block sm:inline ml-2">{error}</span>
          </div>
        )}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-blue-600 font-medium">Task List</h2>
            <div className="flex gap-2">
              
            {/* Updated Download Button */}
            <button 
                  onClick={exportToPDF}
                  disabled={downloading}
                  className={`flex items-center gap-2 p-2 px-3 rounded border transition-colors ${
                    downloading 
                      ? 'bg-blue-50 border-blue-200 cursor-not-allowed' 
                      : 'hover:bg-gray-100 border-transparent'
                  }`}
                >
                  <Download className={`w-4 h-4 ${downloading ? 'animate-bounce text-blue-500' : 'text-gray-600'}`} />
                  <span className={`text-sm font-medium ${downloading ? 'text-blue-600' : 'text-gray-600'}`}>
                    {downloading ? 'Generating PDF...' : 'Export to PDF'}
                  </span>
                </button>
          

              <button className="p-2 hover:bg-gray-100 rounded" onClick={fetchTasks} disabled={loading}>
                <RefreshCw className={`w-4 h-4 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button className="p-2 hover:bg-gray-100 rounded">
                <MoreHorizontal className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {tasks.map((task) => (
              <div 
                key={task.id} 
                className={`min-w-[230px] border-2 rounded-lg p-3 cursor-pointer transition-all ${
                  activeScanId === task.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
                onClick={() => setActiveScanId(task.id)}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="max-w-xs truncate text-sm font-medium text-gray-700" title={task.name}>{task.name}</span>
                  
                  {task.status && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1.5 rounded">
                      {task.status}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-5">{task.time}</div>
              </div>
            ))}
          </div>
        </div>

        {reportLoading && <div className="text-center p-10">Loading report...</div>}
        {reportError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg relative mb-6" role="alert">
            <strong className="font-bold">Error:</strong>
            <span className="block sm:inline ml-2">{reportError}</span>
          </div>
        )}

        {reportData && !reportLoading && (
          <>

        {/* Overview Section */}
        {/* <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-blue-600 font-medium">Overview</h2>
            <button className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700">
              <Download className="w-4 h-4" />
              Export PDF
            </button>
          </div>
          <div className="grid grid-cols-3 gap-8 text-sm">
            <div>
              <div className="text-gray-500 mb-1">Date</div>
              <div className="text-gray-900">{reportData.basicInfo.date}</div>
            </div>
            <div>
              <div className="text-gray-500 mb-1">Target LLM</div>
              <div className="text-gray-900">{reportData.basicInfo.targetLLM}</div>
            </div>
            <div>
              <div className="text-gray-500 mb-1">Scan ID</div>
              <div className="text-gray-900">{reportData.basicInfo.scanId}</div>
            </div>
          </div>
        </div> */}

        {/* Main Content Grid */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          {/* Vulnerability Levels Chart */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-blue-600 font-medium mb-6">Vulnerability Levels Across Risk Categories</h3>
            

            {/* Radar Chart */}
            <div className="relative h-64 mb-10 mt-10">
              {radarChartData && (
                <div className="flex items-center justify-center w-full h-full">
                  <ResponsiveContainer width="100%" height={320}>
                    <RadarChart data={ radarChartData.categories.map((category, index) => ({
                          subject: category,
                          value: radarChartData.values[index]
                        }))}
                        // margin={{ top: 20, right: 50, bottom: 20, left: 50 }}
                         outerRadius="60%"
                        >
                      <PolarGrid stroke="#e0e0e0" />
                      <PolarAngleAxis 
                        dataKey="subject" 
                        tick={{ fill: '#666', fontSize: 12 }}
                        tickLine={false}
                      />
                      <PolarRadiusAxis 
                        angle={90} 
                        domain={[0, 100]}
                        tick={{ fill: '#999', fontSize: 10 }}
                      />
                      <Radar
                        name="Threat Level"
                        dataKey="value"
                        stroke="#7c3aed"
                        fill="#a78bfa"
                        fillOpacity={0.5}
                        strokeWidth={2}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
            
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">{reportData.securityScore}%</div>
            </div>
          </div>

          {/* Risk Levels Overview */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-blue-600 font-medium mb-6">Risk Levels Overview</h3>
            
            <div className="flex items-center justify-left mb-6">
              {/* Donut Chart */}
              
              
              {/* Key Insights */}
              <div className="ml-8 space-y-2 text-sm">
                {reportData.keyInsights.map((insight, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <span className=" text-blue-500">•</span>
                    <div className="text-gray-600" dangerouslySetInnerHTML={{ __html: insight.replace(/`([^`]+)`/g, '<span class="font-semibold text-gray-800">$1</span>') }} />
                  </div>
                ))}
              </div>
            </div>

            {/* Risk Level Cards */}
            <div className="grid grid-cols-2 gap-4">
              {riskBreakdown.map(item => (
                <div key={item.level} className={`border rounded-lg p-4 ${
                  item.level === 'critical' ? 'border-red-200 bg-red-50' : 
                  item.level === 'high' ? 'border-orange-200 bg-orange-50' : 
                  item.level === 'moderate' ? 'border-blue-200 bg-blue-50' : 'border-green-200 bg-green-50'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className={`w-5 h-5 ${
                      item.level === 'critical' ? 'text-red-600' : 
                      item.level === 'high' ? 'text-orange-600' : 
                      item.level === 'moderate' ? 'text-blue-600' : 'text-green-600'
                    }`} />
                    <span className="font-medium text-gray-900">{item.label}</span>
                    <span className={`ml-auto font-semibold ${
                      item.level === 'critical' ? 'text-red-600' : 
                      item.level === 'high' ? 'text-orange-600' : 
                      item.level === 'moderate' ? 'text-blue-600' : 'text-green-600'
                    }`}>{item.count}</span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{item.percentage}%</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Flagged AI Response Examples */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-blue-600 font-medium">Flagged AI Response Examples</h3>
            <select 
              className="border border-gray-300 rounded px-3 py-1 text-sm text-gray-600"
              value={activeCategory}
              onChange={(e) => setActiveCategory(e.target.value)}
            >
              {reportData.riskCategories.map(cat => (
                <option key={cat.key} value={cat.key}>{cat.label}</option>
              ))}
            </select>
          </div>

          <div className="space-y-3">
            {flaggedExamples.map((example, index) => (
              <div key={example.prompt} className={`${example.is_jailbroken ? 'bg-gray-50' : 'bg-green-50'} rounded-lg p-4`}>
                <div className="flex items-start justify-between min-w-0">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-white text-sm font-medium text-gray-700">
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 mb-2 break-words">{example.text}</div>
                      {expandedItems[index] && (
                        <div className="text-sm text-gray-600 mt-2">
                          {!/multi.?turn/i.test(reportData?.basicInfo?.method ?? '') && (
                            <>
                              <strong>Prompt:</strong> <span className="break-words [&_pre]:overflow-x-auto [&_pre]:whitespace-pre-wrap [&_code]:break-words"><ReactMarkdown remarkPlugins={[remarkGfm]}>{example.prompt}</ReactMarkdown></span><br/><br/>
                            </>
                          )}
                          <strong>Response:</strong>
                          <div className="break-words [&_pre]:overflow-x-auto [&_pre]:whitespace-pre-wrap [&_code]:break-words">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{example.details}</ReactMarkdown>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <button 
                    onClick={() => toggleExpand(index)}
                    className="ml-4 text-gray-400 hover:text-gray-600"
                  >
                    <ChevronDown className={`w-5 h-5 transition-transform ${expandedItems[index] ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default AssessmentReport;